import type { SerializedStyles } from "@emotion/serialize";
import { Button, Message, Typography } from "@fullscript/aviary-web";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useCopyToClipboard } from "@shared/hooks/useCopyToClipboard/useCopyToClipboard";
import { useIsWebview } from "@shared/hooks/useIsWebview/useIsWebview";
import { useLastUsedLoginMethod } from "@shared/hooks/useLastUsedLoginMethod/useLastUsedLoginMethod";
import { l } from "@shared/locales/i18n";
import type { PatientAdditionalAttributesType } from "@shared/types/PatientAdditionalAttributesType";
import type { PractitionerAdditionalAttributesType } from "@shared/types/PractitionerAdditionalAttributesType";
import type { SignInAdditionalAttributesType } from "@shared/types/SignInAdditionalAttributesType";

import { LastUsedPillOverlay } from "../LastUsedPill/LastUsedPillOverlay";
import * as layoutStyles from "../loginLayout.styles";
import { getActionURL } from "../utils/getActionURL";

import GoogleGSVGComponent from "./assets/GoogleG";

import * as styles from "./GoogleSSOButton.styles";

interface Props {
  formData?: {
    firstName?: string;
    lastName?: string;
    storeName?: string;
    tosAcceptedAt?: string;
  };
  treatmentPlanSlug?: string;
  labRecommendationId?: string;
  storeSlug?: string;
  pracTypeId?: string;
  signUpPage?: string;
  pageVersion?: string;
  disabled?: boolean;
  isOrTextRendered?: boolean;
  additionalAttributes?: SignInAdditionalAttributesType &
    PractitionerAdditionalAttributesType &
    PatientAdditionalAttributesType;
  uuid?: string;
  buttonStyles?: SerializedStyles;
  isIntake?: boolean;
  requestedPractitionerId?: string;
  className?: string;
  isLastUsedEnabled?: boolean;
}

const GoogleSSOButton = ({
  formData,
  signUpPage,
  treatmentPlanSlug,
  labRecommendationId,
  storeSlug,
  pracTypeId,
  pageVersion,
  disabled,
  isOrTextRendered,
  additionalAttributes,
  uuid,
  buttonStyles,
  isIntake,
  requestedPractitionerId,
  className,
  isLastUsedEnabled,
  ...rest
}: Props) => {
  const { t } = useTranslation();
  const copyToClipboard = useCopyToClipboard();
  const lastUsedLoginMethod = useLastUsedLoginMethod();
  const isLastUsed = lastUsedLoginMethod === "google";

  const csrf = document.querySelector("meta[name='csrf-token']")?.getAttribute("content") ?? "";
  const buttonText = t(l.googleSSO.ContinueWithGoogle);
  const submitInputId = "google-sso-submit";

  const formStyles = [
    styles.form,
    isLastUsedEnabled && layoutStyles.fullWidth,
    isLastUsedEnabled && layoutStyles.buttonHeight,
    disabled && styles.formDisabled,
    buttonStyles,
  ];
  const submitStyles = [styles.submit, disabled && styles.submitDisabled];
  const logoStyles = [styles.logo, disabled && styles.disabled];
  const googleSSOButtonStyles = [
    styles.GoogleSSOButton,
    isLastUsedEnabled && layoutStyles.fullWidth,
    disabled && styles.disabled,
  ];
  const isWebview = useIsWebview();

  const [logInWithGoogleClickedOnWebview, setLogInWithGoogleClickedOnWebview] = useState(false);

  const handleCopyLink = async () => {
    const { redirectPath } = additionalAttributes ?? {};
    if (redirectPath?.startsWith("http")) {
      await copyToClipboard(redirectPath, { successMessage: t(l.sharedCommon.linkCopied) });
    } else {
      await copyToClipboard(window.location.href, { successMessage: t(l.sharedCommon.linkCopied) });
    }
  };

  if (isWebview) {
    return (
      <LastUsedPillOverlay enabled={!!isLastUsedEnabled} isActive={isLastUsed}>
        <div
          css={styles.buttonWrapper}
          className={className}
          onClick={() => setLogInWithGoogleClickedOnWebview(true)}
        >
          <div css={formStyles} {...rest}>
            <label aria-label={buttonText} css={googleSSOButtonStyles} htmlFor={submitInputId}>
              <GoogleGSVGComponent css={logoStyles} />
              <input
                id={submitInputId}
                value={buttonText}
                type="submit"
                css={submitStyles}
                disabled={disabled}
              />
            </label>
          </div>
          {!!logInWithGoogleClickedOnWebview && (
            <Message
              intention="neutral"
              customCss={styles.continueInBrowserMessage}
              label={
                <Typography isMarginless>{t(l.googleSSO.ContinueWithGoogleInBrowser)}</Typography>
              }
            >
              <Button
                size="small"
                intention="neutral"
                aria-label={t(l.sharedCommon.copyLink)}
                onClick={handleCopyLink}
                variant="primary"
              >
                {t(l.sharedCommon.copyLink)}
              </Button>
            </Message>
          )}
        </div>
      </LastUsedPillOverlay>
    );
  }

  return (
    <>
      <link rel="preconnect" href="https://fonts.gstatic.com"></link>
      <link
        href="https://fonts.googleapis.com/css2?family=Roboto:wght@500&display=swap"
        rel="stylesheet"
      ></link>
      <LastUsedPillOverlay enabled={!!isLastUsedEnabled} isActive={isLastUsed}>
        <div css={styles.buttonWrapper} className={className} data-e2e="new-sign-in-flow">
          <div css={formStyles} {...rest}>
            <form
              action={getActionURL(
                "Google",
                formData,
                additionalAttributes,
                signUpPage,
                treatmentPlanSlug,
                labRecommendationId,
                storeSlug,
                pracTypeId,
                pageVersion,
                uuid,
                isIntake,
                requestedPractitionerId
              )}
              method="post"
              data-e2e="google-sso-button"
            >
              <label aria-label={buttonText} css={googleSSOButtonStyles} htmlFor={submitInputId}>
                <GoogleGSVGComponent css={logoStyles} />
                <input
                  id={submitInputId}
                  value={buttonText}
                  type="submit"
                  css={submitStyles}
                  disabled={disabled}
                />
              </label>
              <input type="hidden" name="authenticity_token" value={csrf}></input>
            </form>
          </div>
          {!!isOrTextRendered && (
            <Typography
              variant="footnote"
              isMarginless
              customCss={styles.orSection}
              intention="neutral"
              colorIntensity="subtle"
            >
              {t(l.googleSSO.Or)}
            </Typography>
          )}
        </div>
      </LastUsedPillOverlay>
    </>
  );
};

export { GoogleSSOButton };
